import { Button, Modal, InputDeprecated, InlineAlert } from '@/components';
import { useForm } from 'react-hook-form';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import * as React from 'react';
import { useAtom } from 'jotai/index';
import { upsertNnWeightLogAtom } from '../model/upsertNnWeightLogAtom';
import { deleteNnWeightLogAtom } from '../model/deleteNnWeightLogAtom';
import { formatDateForInput } from '@/shared/helpers/formatDateForInput';

export type EditDeleteWeightLogModalProps = {
    onClose: () => void;
    minDate: Date;
    weightLog: {
        id: number;
        weightKg: number;
        formattedDate: string;
        date: string;
        isFirstLog: boolean;
    };
};

export type IFormEditDeleteWeightLog = {
    WeightKg: number;
    Date: string;
    Id: number;
};

export const EditDeleteWeightLogModal = (
    props: EditDeleteWeightLogModalProps,
) => {
    const { weightLog } = props;

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IFormEditDeleteWeightLog>({
        mode: 'onSubmit',
        defaultValues: {
            WeightKg: weightLog.weightKg,
            Id: weightLog.id,
            Date: formatDateForInput(new Date(weightLog.date)),
        },
    });

    const [
        {
            mutate: upsertMutate,
            status: upsertStatus,
            reset: upsertReset,
            error: upsertError,
        },
    ] = useAtom(upsertNnWeightLogAtom);

    const [
        {
            mutate: deleteMutate,
            status: deleteStatus,
            reset: deleteReset,
            error: deleteError,
        },
    ] = useAtom(deleteNnWeightLogAtom);

    // hide validation errors once the user types again
    const [showErrors, setShowErrors] = React.useState(false);

    const onSubmit = (data: IFormEditDeleteWeightLog) => {
        upsertMutate(data, {
            onSuccess: () => props.onClose(),
        });
    };

    const onDelete = () => {
        if (!weightLog.id) return;
        deleteMutate(weightLog.id, {
            onSuccess: () => props.onClose(),
        });
    };

    const onError = () => {
        setShowErrors(true);
    };

    return (
        <Modal
            onClose={() => {
                upsertReset();
                deleteReset();
                props.onClose();
            }}
            title={R.SundvaegtWeightEditWeightLogLabel}
        >
            {upsertStatus !== 'error' && deleteStatus !== 'error' && (
                <>
                    <form onSubmit={handleSubmit(onSubmit, onError)}>
                        <InputDeprecated
                            type="date"
                            min={formatDateForInput(props.minDate)}
                            max={formatDateForInput(new Date())}
                            label={R.SundvaegtDateInputLabel}
                            inputClassName={`${showErrors && errors.Date ? 'border border-secondary-red' : ''}`}
                            containerClassName="mt-8"
                            {...register('Date', {
                                required: R.SundvaegtDateInputRequiredLabel,
                                onChange: () => setShowErrors(false),
                            })}
                            error={
                                showErrors ? errors.Date?.message : undefined
                            }
                        />

                        <InputDeprecated
                            type="number"
                            step="0.1"
                            suffix="kg"
                            placeholder={
                                R.SundvaegtAddWeightLogInputPlaceholder
                            }
                            label={R.SundvaegtAddWeightLogModalInputLabel}
                            inputClassName={`${showErrors && errors.WeightKg ? 'border border-secondary-red' : ''}`}
                            containerClassName="mt-2"
                            {...register('WeightKg', {
                                required:
                                    R.SundvaegtAddWeightLogModalInputRequiredLabel,
                                min: {
                                    value: 40,
                                    message:
                                        R.SundvaegtPleaseEnterValidWeightLabel,
                                },
                                max: {
                                    value: 400,
                                    message:
                                        R.SundvaegtPleaseEnterValidWeightLabel,
                                },
                                onChange: () => setShowErrors(false),
                                valueAsNumber: true,
                            })}
                            error={
                                showErrors
                                    ? errors.WeightKg?.message
                                    : undefined
                            }
                        />

                        <div
                            className={`mt-8 flex w-full flex-row ${weightLog.isFirstLog ? 'justify-end' : 'justify-between'}`}
                        >
                            {!weightLog.isFirstLog && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={onDelete}
                                    disabled={deleteStatus === 'pending'}
                                    className="text-secondary-red"
                                >
                                    {R.SundvaegtWeightDeleteWeightLogLabel}
                                </Button>
                            )}

                            <Button
                                variant="primary"
                                type="submit"
                                disabled={upsertStatus === 'pending'}
                            >
                                {R.SundvaegtAddWeightLogModalSubmitButton}
                            </Button>
                        </div>
                    </form>
                </>
            )}
            {upsertStatus === 'error' && (
                <InlineAlert
                    onClose={upsertReset}
                    errorMessage={upsertError?.message}
                />
            )}
            {deleteStatus === 'error' && (
                <InlineAlert
                    onClose={deleteReset}
                    errorMessage={deleteError?.message}
                />
            )}
        </Modal>
    );
};
